import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Space, Tag, Typography, message } from 'antd';
import { BaseViewContext } from '../../views/Base';
import UserService from '../../services/User';
import PhoneVerificationModal from '../phone-verification/PhoneVerificationModal';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const EditUserInfoModal = ({
  open,
  user,
  onCancel,
  onUserUpdated,
}) => {
  const { t } = useTranslation('translation');
  const { base_view_ref } = useContext(BaseViewContext);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [phoneVerificationOpen, setPhoneVerificationOpen] = useState(false);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState('');

  const initialPhoneRef = useRef('');
  const initialNameRef = useRef('');

  const watchedPhone = Form.useWatch('phoneNumber', form);
  const displayPhone = (watchedPhone || '').trim();

  useEffect(() => {
    if (!open || !user) return;

    const phoneNumber = user.phoneNumber || user.phone || '';
    const name = user.name || '';

    initialPhoneRef.current = phoneNumber;
    initialNameRef.current = name;
    setVerifiedPhoneNumber(user.phoneVerified ? phoneNumber : '');

    form.setFieldsValue({
      name,
      email: user.email || '',
      phoneNumber,
    });
  }, [open, user, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    const nextPhone = (values.phoneNumber || '').trim();
    const phoneChanged = nextPhone !== (initialPhoneRef.current || '');
    const phoneVerified = !phoneChanged || verifiedPhoneNumber === nextPhone;

    if (phoneChanged && !phoneVerified) {
      if (!nextPhone) {
        message.error(t('general.phone_required'));
        return;
      }
      setPhoneVerificationOpen(true);
      return;
    }

    const updatePayload = {};
    if (values.name !== initialNameRef.current) {
      updatePayload.name = values.name;
    }

    if (Object.keys(updatePayload).length === 0) {
      onCancel?.();
      return;
    }

    setSaving(true);
    try {
      const updatedUser = await UserService.updateUser(user.id, updatePayload);
      onUserUpdated?.(updatedUser);
      message.success(t('general.user_info_updated'));
      onCancel?.();
    } catch (error) {
      message.error(t('general.user_info_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneVerified = (phoneNumber) => {
    setVerifiedPhoneNumber(phoneNumber);
    form.setFieldsValue({ phoneNumber });
    onUserUpdated?.({
      ...user,
      phoneNumber,
      phoneVerified: true,
      phoneVerifiedAt: new Date().toISOString(),
    });
    setPhoneVerificationOpen(false);
    message.success(t('general.phone_verified'));
  };

  const phoneChanged = displayPhone !== (initialPhoneRef.current || '');
  const phoneStatus = phoneChanged ? (verifiedPhoneNumber === displayPhone ? 'verified' : 'pending') : (user?.phoneVerified ? 'verified' : 'unverified');

  return (
    <>
      <Modal
        open={open}
        title={t('general.edit_user_info')}
        centered
        destroyOnClose
        onCancel={onCancel}
        onOk={handleSave}
        okText={t('general.save')}
        okButtonProps={{ loading: saving }}
        getContainer={() => base_view_ref.current}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('general.name')}
            name="name"
            rules={[{ required: true, message: t('general.name_required') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('general.email')} name="email">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('general.phone')}
            name="phoneNumber"
            rules={[{ required: false }]}
          >
            <Input placeholder={t('general.phone_placeholder')} />
          </Form.Item>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Tag color={phoneStatus === 'verified' ? 'green' : phoneStatus === 'pending' ? 'orange' : 'default'}>
              {phoneStatus === 'verified' && t('general.phone_verified')}
              {phoneStatus === 'pending' && t('general.phone_verification_required')}
              {phoneStatus === 'unverified' && t('general.phone_unverified')}
            </Tag>
            {phoneChanged && verifiedPhoneNumber !== displayPhone && displayPhone && (
              <Text type="secondary">{t('general.phone_verification_needed')}</Text>
            )}
            {phoneChanged && displayPhone && (
              <Button onClick={() => setPhoneVerificationOpen(true)}>
                {t('general.verify_phone_number')}
              </Button>
            )}
          </Space>
        </Form>
      </Modal>

      <PhoneVerificationModal
        open={phoneVerificationOpen}
        phoneNumber={displayPhone}
        onCancel={() => setPhoneVerificationOpen(false)}
        onVerified={handlePhoneVerified}
      />
    </>
  );
};

export default EditUserInfoModal;
